import React from 'react';
import type { BondSummary, SlashingEvent } from '../types';
import { formatCurrency, formatDate, formatDid } from '../utils/format';

export interface BondManagerProps {
  bonds: BondSummary;
  slashingEvents?: SlashingEvent[];
  hopsEarnings?: number;
  className?: string;
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onAppeal?: (eventId: string) => void;
}

/**
 * Bond & Stake Manager Panel
 * Shows locked capital, slashing history, HOPs earnings
 */
export function BondManager({
  bonds,
  slashingEvents = [],
  hopsEarnings = 0,
  className = '',
  onDeposit,
  onWithdraw,
  onAppeal,
}: BondManagerProps) {
  const recentSlashes = slashingEvents.slice(0, 5);

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">Bond & Stake Manager</h2>
        <div className="flex gap-2">
          {onDeposit && (
            <button
              onClick={onDeposit}
              className="px-3 py-1 text-xs bg-green-600 hover:bg-green-500 text-white rounded transition-colors"
            >
              Deposit
            </button>
          )}
          {onWithdraw && (
            <button
              onClick={onWithdraw}
              className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
            >
              Withdraw
            </button>
          )}
        </div>
      </div>

      {/* Total Locked */}
      <div className="mb-6">
        <div className="text-sm text-gray-400 mb-1">Total Locked</div>
        <div className="text-3xl font-bold text-white">
          {formatCurrency(bonds.totalLocked)}
        </div>
      </div>

      {/* Bond Breakdown */}
      <div className="space-y-2 mb-6 pl-4 border-l-2 border-gray-700">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Tier 2 Bonds</span>
          <span className="text-white">
            {formatCurrency(bonds.tier2Bonds)}
            <span className="text-gray-500 ml-1">({bonds.tier2Count} agents)</span>
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Tier 3 Bonds</span>
          <span className="text-white">
            {formatCurrency(bonds.tier3Bonds)}
            <span className="text-gray-500 ml-1">({bonds.tier3Count} agents)</span>
          </span>
        </div>
        {bonds.availableBalance > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Available Balance</span>
            <span className="text-green-400">{formatCurrency(bonds.availableBalance)}</span>
          </div>
        )}
      </div>

      {/* Slashing Events */}
      <div className="mb-6">
        <div className="text-sm text-gray-400 mb-2">Slashing Events (30d)</div>
        {recentSlashes.length === 0 ? (
          <div className="text-sm text-gray-500 py-2">No slashing events</div>
        ) : (
          <div className="space-y-1">
            {recentSlashes.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between text-sm bg-gray-800/50 rounded px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">{formatDate(event.timestamp)}</span>
                  <span className="text-gray-300 font-mono text-xs">
                    {formatDid(event.agentDid, 6)}
                  </span>
                  <span className="px-2 py-0.5 text-xs bg-red-900/50 text-red-300 rounded">
                    {event.reason}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-400">-{formatCurrency(event.amount)}</span>
                  {event.appealed ? (
                    <span className={`text-xs ${
                      event.appealStatus === 'granted' ? 'text-green-400' :
                      event.appealStatus === 'denied' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {event.appealStatus === 'granted' ? '✓' :
                       event.appealStatus === 'denied' ? '✗' : '⏳'}
                    </span>
                  ) : onAppeal ? (
                    <button
                      onClick={() => onAppeal(event.id)}
                      className="text-xs text-purple-400 hover:text-purple-300"
                    >
                      Appeal
                    </button>
                  ) : (
                    <span className="text-green-400">✓</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HOPs Earnings */}
      <div className="border-t border-gray-800 pt-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-400">HOPs Earnings (MTD)</span>
          <span className="text-lg font-semibold text-green-400">
            {formatCurrency(hopsEarnings)}
          </span>
        </div>
      </div>
    </div>
  );
}
